import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@arcadeai/design-system/components/ui/atoms/dialog";
import { Loader2, Trash2 } from "lucide-react";
import { useState } from "react";
import { useDeleteServer } from "@/app/servers/api/hooks";

type DeleteServerButtonProps = {
  id: string;
  name: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  showTrigger?: boolean;
};

export const DeleteServerButton = ({
  id,
  name,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  showTrigger = true,
}: DeleteServerButtonProps) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const { mutate: deleteServer, isPending } = useDeleteServer();

  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : uncontrolledOpen;
  const setIsOpen = isControlled
    ? (controlledOnOpenChange ?? (() => undefined))
    : setUncontrolledOpen;

  const handleDelete = () => {
    deleteServer(id, {
      onSuccess: () => {
        setIsOpen(false);
      },
    });
  };

  return (
    <Dialog onOpenChange={setIsOpen} open={isOpen}>
      {!!showTrigger && (
        <DialogTrigger
          render={
            <Button
              className="text-destructive hover:text-destructive/80 active:text-destructive/60"
              disabled={isPending}
              size="sm"
              variant="ghost"
            />
          }
        >
          <Trash2 className="size-4" />
          <span>Delete Server</span>
        </DialogTrigger>
      )}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete Server</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete {name}? This action cannot be
            undone.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4 flex justify-end gap-2">
          <Button onClick={() => setIsOpen(false)} size="sm" variant="outline">
            Cancel
          </Button>
          <Button
            disabled={isPending}
            onClick={handleDelete}
            size="sm"
            variant="destructive"
          >
            {isPending ? <Loader2 className="size-4 animate-spin" /> : "Delete"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
